import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Box, Button, Container, IconButton, InputAdornment, Stack, TextField, Typography, Paper } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import Styles from '../common/styles/styles';
import { validatePassword, validateConfirmPassword } from '../common/utils/validationUtil';

export default function EmailVerification() {
  console.log('PasswordResetPage component is mounting');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  
  const [errors, setErrors] = useState({});
  const [showPasswords, setShowPasswords] = useState({
    password: false,
    confirmPassword: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  
  const isFormValid = Object.values(errors).filter(value => value !== '').length > 0 || 
                     !formData.password || !formData.confirmPassword;

  const textFieldStyles = Styles.textFieldStyles;

  useEffect(() => {
    // Check if token exists
    if (!token) {
      toast.error('Invalid password reset link. Please request a new password reset.');
      navigate('/login');
      return;
    }
    
    setTokenValid(true);
  }, [token, navigate]);

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData({ ...formData, [field]: value });
    
    const newErrors = { ...errors };
    
    if (field === 'password') {
      newErrors.password = validatePassword(value);
      // Re-validate confirm password if it exists
      if (formData.confirmPassword) {
        newErrors.confirmPassword = validateConfirmPassword(value, formData.confirmPassword);
      }
    }
    if (field === 'confirmPassword') {
      newErrors.confirmPassword = validateConfirmPassword(formData.password, value);
    }
    
    setErrors(newErrors);
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const resetPassword = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API}/users/reset-password-with-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          newPassword: formData.password
        }),
      });

      const result = await response.json();

      if (result.message === 'success') {
        toast.success('Password reset successful! You can now log in with your new password.', { autoClose: 5000 });
        navigate('/login');
      } else {
        toast.error(result.message);
        if (result.message.includes('Invalid or expired')) {
          setTimeout(() => navigate('/'), 3000);
        }
      }
    } catch (error) {
      console.error('Password reset error:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    event.stopPropagation();
    resetPassword();
  };

  if (tokenValid === null) {
    return (
      <Container component="main" maxWidth="xs" sx={{ mt: 15 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <Typography variant="h6" color="white">Validating reset link...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs" sx={{ mt: 15 }}>
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography component="h1" variant="h2" m={4} color="white">
          CheckMate
        </Typography>
        
        <Box
          component={Paper}
          elevation={3}
          sx={{
            width: '100%',
            maxWidth: 400,
            bgcolor: '#1e1e1e',
            p: 4,
            borderRadius: 2
          }}
        >
          <Typography variant="h4" component="h2" color="white" mb={3} textAlign="center">
            Set New Password
          </Typography>
          
          <Typography variant="body2" color="white" sx={{ mb: 3, opacity: 0.8, textAlign: 'center' }}>
            Enter your new password below.
          </Typography>
          
          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <TextField
                required
                fullWidth
                label="New Password"
                type={showPasswords.password ? 'text' : 'password'}
                value={formData.password}
                onChange={handleInputChange('password')}
                error={!!errors.password}
                helperText={errors.password}
                sx={textFieldStyles}
                disabled={isLoading}
                slotProps={{
                  input: {
                    autoComplete: 'new-password',
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => togglePasswordVisibility('password')}
                          edge="end"
                          sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                          disabled={isLoading}
                        >
                          {showPasswords.password ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }
                }}
              />

              <TextField
                required
                fullWidth
                label="Confirm New Password"
                type={showPasswords.confirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleInputChange('confirmPassword')}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                sx={textFieldStyles}
                disabled={isLoading}
                slotProps={{
                  input: {
                    autoComplete: 'new-password',
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => togglePasswordVisibility('confirmPassword')}
                          edge="end"
                          sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                          disabled={isLoading}
                        >
                          {showPasswords.confirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }
                }}
              />
              
              <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={isFormValid || isLoading}
                  sx={{ 
                        ...Styles.buttonStyle,
                        width: '100%'

                   }}
              >
                  {isLoading ? 'Resetting Password...' : 'Reset Password'}
              </Button>

              <Button
                fullWidth
                variant="text"
                onClick={() => navigate('/')}
                disabled={isLoading}
                sx={{ 
                    ...Styles.buttonStyle,
                    width: '100%',
                    color: '#fff'
                }}
              >
                Back to Log In
              </Button>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}